import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../api/client";
import Tabs from "../../components/Tabs";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import { usePermission } from "../../../../hooks/usePermission";
import { PERMISSIONS } from "../../../../constants/permissions";
import useAuth from "../../../../hooks/useAuth";
import LockableButton from "../../components/LockableButton";
import { verifyModuleEnabled } from "../../utils/moduleAccess";
import AllottedTaskModal from "./AllottedTaskModal";
import FixActionDialog from "./FixActionDialog";
import "./AllottedTasks.css";

// How many past runs each cadence strip shows.
const CADENCE_LENGTH = 12;

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function overdueBy(iso) {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Photos are served behind the same access token as other uploads; an <img>
// cannot send a header, so it travels in the query string.
function mediaUrl(filePath) {
  return `${process.env.REACT_APP_API_UPLOAD_URL || ""}${filePath}?token=${encodeURIComponent(
    localStorage.getItem("accessToken") || "",
  )}`;
}

function markClass(occ) {
  if (occ.status === "missed") return "missed";
  if (occ.status === "superseded") return "superseded";
  if (occ.status === "pending") return "pending";
  if (["text", "number", "photo"].includes(occ.responseType)) return "responded-text";
  if (occ.responseBool) return "responded-yes";
  // A "no" that came with a written reason says more than a bare one, and a
  // reason still owed is a loose end — both need to be visible in the strip.
  if (occ.reasonText) return "responded-no with-reason";
  if (occ.awaitingReason) return "responded-no reason-owed";
  return "responded-no";
}

function markTitle(occ) {
  const when = formatWhen(occ.dueAt);
  if (occ.status === "missed") return `${when} — no answer`;
  if (occ.status === "superseded") return `${when} — reminder, no reply before the next one`;
  if (occ.status === "pending") return `${when} — waiting`;
  if (occ.responseType === "photo") return `${when} — photo${occ.responseText ? `: "${occ.responseText}"` : ""}`;
  if (occ.responseType === "number") return `${when} — ${occ.responseNumber ?? occ.responseText}`;
  if (occ.responseType === "text") return `${when} — "${occ.responseText}"`;
  const answer = occ.responseBool ? "Yes" : "No";
  if (occ.reasonText) return `${when} — ${answer}: “${occ.reasonText}”`;
  if (occ.awaitingReason) return `${when} — ${answer}, reason not given yet`;
  return `${when} — ${answer}`;
}

function Answer({ occ }) {
  // An optional photo added after a Yes / No, text or number answer.
  if (occ.photoPath && occ.responseType !== "photo") {
    return (
      <span className="at-photo-answer">
        <AnswerText occ={occ} />
        <a href={mediaUrl(occ.photoPath)} target="_blank" rel="noreferrer">
          <img src={mediaUrl(occ.photoPath)} alt={`Added to the answer for ${occ.title}`} className="at-photo-thumb" />
        </a>
      </span>
    );
  }
  return <AnswerText occ={occ} />;
}

function AnswerText({ occ }) {
  if (occ.status === "pending") return <span className="at-muted">Waiting</span>;
  if (occ.status === "superseded") return <span className="at-muted">Reminder, no reply</span>;
  if (occ.status === "missed") return <span className="at-answer-no">No answer</span>;
  if (occ.responseType === "photo" && occ.photoPath) {
    return (
      <span className="at-photo-answer">
        <a href={mediaUrl(occ.photoPath)} target="_blank" rel="noreferrer">
          <img src={mediaUrl(occ.photoPath)} alt={`Answer for ${occ.title}`} className="at-photo-thumb" />
        </a>
        {occ.responseText && <span>{occ.responseText}</span>}
      </span>
    );
  }
  if (occ.responseType === "text") return <span>{occ.responseText}</span>;
  if (occ.responseBool) return <span className="at-answer-yes">Yes — completed</span>;
  return (
    <>
      <span className="at-answer-no">No — not completed</span>
      {occ.reasonText && <span className="at-reason">“{occ.reasonText}”</span>}
      {occ.awaitingReason && <span className="at-reason-missing">Reason not given yet</span>}
    </>
  );
}

function CadenceStrip({ task, occurrences }) {
  const byUser = useMemo(() => {
    const map = new Map();
    for (const a of task.assignees) map.set(a.id, []);
    // occurrences arrive newest-first; take the newest CADENCE_LENGTH per person
    for (const occ of occurrences) {
      const bucket = map.get(occ.assignee?.id);
      if (bucket && bucket.length < CADENCE_LENGTH) bucket.push(occ);
    }
    return map;
  }, [task.assignees, occurrences]);

  return (
    <div className="at-cadence">
      {task.assignees.map((a) => {
        const runs = (byUser.get(a.id) || []).slice().reverse(); // oldest → newest
        return (
          <div className="at-cadence-row" key={a.id}>
            <div className="at-cadence-name">
              {a.name}
              {!a.hasTelegram && !a.hasWhatsapp && <span className="at-no-telegram">no messaging channel</span>}
            </div>
            {a.completedAt ? (
              <span className="at-done-note">
                ✓ Done {formatWhen(a.completedAt)} — reminders stopped
              </span>
            ) : runs.length === 0 ? (
              <span className="at-cadence-empty">No runs yet</span>
            ) : (
              <div className="at-marks">
                {runs.map((occ) => (
                  <span key={occ.id} className={`at-mark ${markClass(occ)}`} title={markTitle(occ)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatDay(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function fixStatus(fix) {
  if (fix.status === "fixed") return { cls: "fixed", label: "Fixed", note: "Waiting for the next check-in" };
  if (fix.status === "blocked") {
    // Blocked either by the fixer or by running out of attempts.
    const last = (fix.log || []).filter((e) => e.type === "blocked" || e.type === "exhausted").pop();
    const exhausted = last?.type === "exhausted";
    return {
      cls: "blocked",
      label: exhausted ? `Didn't work after ${fix.attempt} attempts` : "Can't fix",
      note: exhausted ? "Waiting for you" : fix.awaitingBlockReason ? "Reason not given yet" : fix.blockedReason,
    };
  }
  if (fix.attempt > 1) return { cls: "reopened", label: `Didn't work · attempt ${fix.attempt}`, note: null };
  return { cls: "", label: fix.isOverdue ? "Fix overdue" : "Fix open", note: null };
}

function FixRow({ fix, canManage, onAction }) {
  const { cls, label, note } = fixStatus(fix);
  return (
    <div className={`at-fix ${cls}`}>
      <span className="at-fix-status">{label}</span>
      <div className="at-fix-body">
        <span>
          {fix.reason ? `“${fix.reason}”` : "No reason given"} · {fix.reporter?.name}
        </span>
        <span className="at-fix-meta">
          {fix.fixer?.name}
          {fix.status !== "blocked" && <> · by {formatDay(fix.dueAt)}</>}
          {note && <> · {note}</>}
        </span>
      </div>
      {canManage && (
        <div className="at-fix-actions">
          <button type="button" className="at-btn-ghost" onClick={() => onAction(fix, "reassign")}>Reassign</button>
          {fix.status !== "fixed" && (
            <button type="button" className="at-btn-ghost" onClick={() => onAction(fix, "extend")}>More time</button>
          )}
          <button type="button" className="at-btn-ghost" onClick={() => onAction(fix, "close")}>Close fix</button>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, occurrences, canManage, onEdit, onToggle, onDelete, onRunNow, onFixAction, busy }) {
  const pending = task.stats.pending || 0;
  const isChase = task.mode === "until_done";
  const cardClass = [
    "at-card",
    task.isActive ? "" : "paused",
    task.allDone ? "done" : "",
    task.isOverdue ? "overdue" : "",
  ].filter(Boolean).join(" ");

  return (
    <article className={cardClass}>
      <div className="at-card-top">
        <div style={{ minWidth: 0 }}>
          <h3 className="at-card-title">{task.title}</h3>
          {task.description && <p className="at-card-desc">{task.description}</p>}
        </div>
        <div className="at-actions">
          {task.isOverdue && <span className="at-chip overdue">Overdue</span>}
          {task.allDone ? (
            <span className="at-chip live">Done</span>
          ) : (
            <span className={`at-chip ${task.isActive ? "live" : "paused"}`}>
              {task.isActive ? "Running" : "Paused"}
            </span>
          )}
          <span className="at-chip mode">{isChase ? "Until done" : "Recurring"}</span>
          {task.allowPhoto && <span className="at-chip mode">Photo optional</span>}
          <span className="at-chip type">
            {task.responseType === "yes_no"
              ? task.raiseFixOnNo
                ? `No → fix by ${task.fixer?.name || "whoever said No"}`
                : task.askReasonOnNo
                  ? "Yes / No + reason"
                  : "Yes / No"
              : task.responseType === "photo"
                ? "Photo"
                : task.responseType === "number"
                  ? "Number"
                  : "Written"}
          </span>
        </div>
      </div>

      <div className="at-meta">
        <span>{task.scheduleLabel}</span>
        {task.isActive && task.nextDueAt && <span>Next <strong>{formatWhen(task.nextDueAt)}</strong></span>}
        {isChase ? (
          <>
            <span>Done <strong>{task.completedCount} of {task.assignees.length}</strong></span>
            {task.dueBy && (
              <span className={task.isOverdue ? "at-due-late" : undefined}>
                Due by <strong>{formatWhen(task.dueBy)}</strong>
              </span>
            )}
          </>
        ) : (
          <>
            <span>Answered <strong>{task.stats.responded || 0}</strong></span>
            <span>Missed <strong>{task.stats.missed || 0}</strong></span>
          </>
        )}
        {pending > 0 && <span className="at-chip warn">{pending} waiting</span>}
      </div>

      <CadenceStrip task={task} occurrences={occurrences} />

      {task.fixes?.length > 0 && (
        <div className="at-fixes">
          {task.fixes.map((f) => (
            <FixRow key={f.id} fix={{ ...f, title: task.title }} canManage={canManage} onAction={onFixAction} />
          ))}
        </div>
      )}

      {canManage && (
        <div className="at-actions" style={{ marginTop: 14 }}>
          <button type="button" className="at-btn-ghost" onClick={() => onEdit(task)}>Edit</button>
          <button type="button" className="at-btn-ghost" onClick={() => onToggle(task)}>
            {task.isActive ? "Pause" : "Resume"}
          </button>
          <button type="button" className="at-btn-ghost" onClick={() => onRunNow(task)} disabled={busy}>
            Send now
          </button>
          <button type="button" className="at-btn-ghost danger" onClick={() => onDelete(task)}>Delete</button>
        </div>
      )}
    </article>
  );
}

export default function AllottedTasks() {
  const canManage = usePermission(PERMISSIONS.MANUFACTURING_ALLOTTED_TASK_MANAGE);
  const { enabledModules, setEnabledModules } = useAuth();
  const hasTaskManagementModule = !enabledModules || enabledModules.includes("taskManagement");

  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modalTask, setModalTask] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // { fix, action } — reassign / extend open a dialog, close confirms.
  const [fixAction, setFixAction] = useState(null);
  const [fixPeople, setFixPeople] = useState([]);

  const [taskFilter, setTaskFilter] = useState("");
  const [personFilter, setPersonFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [taskRes, occRes] = await Promise.all([
        api.get("/manufacturing/allotted-tasks"),
        api.get("/manufacturing/allotted-tasks/occurrences", { params: { limit: 500 } }),
      ]);
      setTasks(taskRes.data.data || []);
      setOccurrences(occRes.data.data || []);
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Couldn't load allotted tasks.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reassigning a fix can pick anyone, not only the task's assignees.
  useEffect(() => {
    if (!canManage) return;
    api
      .get("/manufacturing/allotted-tasks/assignable-users")
      .then((res) => setFixPeople(res.data.data || []))
      .catch(() => {});
  }, [canManage]);

  const handleCloseFix = async () => {
    try {
      await api.post(`/manufacturing/allotted-tasks/fixes/${fixAction.fix.id}/close`);
      setToast({ msg: "Fix closed", type: "success" });
      setFixAction(null);
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Couldn't close the fix.", type: "error" });
    }
  };

  const occurrencesByTask = useMemo(() => {
    const map = new Map();
    for (const occ of occurrences) {
      if (!map.has(occ.taskId)) map.set(occ.taskId, []);
      map.get(occ.taskId).push(occ);
    }
    return map;
  }, [occurrences]);

  const people = useMemo(() => {
    const map = new Map();
    for (const t of tasks) for (const a of t.assignees) map.set(a.id, a.name);
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  const filtered = useMemo(
    () => occurrences.filter(
      (o) => (!taskFilter || o.taskId === taskFilter) && (!personFilter || o.assignee?.id === personFilter),
    ),
    [occurrences, taskFilter, personFilter],
  );

  const waiting = useMemo(
    () => filtered.filter((o) => o.status === "pending").sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt)),
    [filtered],
  );
  const answered = useMemo(() => filtered.filter((o) => o.status !== "pending"), [filtered]);

  const handleToggle = async (task) => {
    try {
      await api.patch(`/manufacturing/allotted-tasks/${task.id}`, { isActive: !task.isActive });
      setToast({ msg: task.isActive ? "Task paused" : "Task resumed", type: "success" });
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Couldn't update the task.", type: "error" });
    }
  };

  const handleRunNow = async (task) => {
    setBusyId(task.id);
    try {
      const res = await api.post(`/manufacturing/allotted-tasks/${task.id}/run-now`);
      const sent = res.data.data?.sent ?? 0;
      setToast({
        msg: sent > 0 ? `Sent to ${sent} ${sent === 1 ? "person" : "people"}` : "Everyone already has this check-in open",
        type: "success",
      });
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Couldn't send the check-in.", type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/manufacturing/allotted-tasks/${confirmDelete.id}`);
      setToast({ msg: "Task deleted", type: "success" });
      setConfirmDelete(null);
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Couldn't delete the task.", type: "error" });
    }
  };

  const handleNewTaskClick = async () => {
    const { allowed, message } = await verifyModuleEnabled("taskManagement", { enabledModules, setEnabledModules });
    if (!allowed) {
      setToast({ msg: message, type: "error" });
      return;
    }
    setModalTask(null);
    setModalOpen(true);
  };

  const totalWaiting = occurrences.filter((o) => o.status === "pending").length;

  const tabs = [
    { key: "tasks", label: `Tasks (${tasks.length})` },
    { key: "waiting", label: `Awaiting reply (${totalWaiting})` },
    { key: "responses", label: "Responses" },
  ];

  return (
    <div className="at-page">
      <Link className="back-link" to="/manufacturing/my-tasks">← Back to Tasks</Link>

      <div className="at-head">
        <div>
          <h1 className="at-title">Assigned tasks</h1>
          <p className="at-sub">Recurring check-ins your team answers on WhatsApp or in the app.</p>
        </div>
        {canManage && (
          <LockableButton
            className="at-btn-primary"
            onClick={handleNewTaskClick}
            locked={!hasTaskManagementModule}
            lockedTitle="Task Management module is not enabled for your company"
          >
            + New task
          </LockableButton>
        )}
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <div className="at-empty">Loading…</div>
      ) : tab === "tasks" ? (
        tasks.length === 0 ? (
          <div className="at-empty">
            <strong>No allotted tasks yet</strong>
            {canManage
              ? "Create one to start checking in with your team on a schedule."
              : "Nothing has been set up for your organisation yet."}
          </div>
        ) : (
          <>
            <p className="at-legend">
              <span><i className="at-mark responded-yes" /> Yes</span>
              <span><i className="at-mark responded-no" /> No</span>
              <span><i className="at-mark responded-no with-reason" /> No, with reason</span>
              <span><i className="at-mark responded-no reason-owed" /> No, reason still owed</span>
              <span><i className="at-mark responded-text" /> Written reply</span>
              <span><i className="at-mark pending" /> Waiting</span>
              <span><i className="at-mark missed" /> No answer</span>
              <span><i className="at-mark superseded" /> Reminder, no reply</span>
              <span className="at-muted">Oldest run on the left</span>
            </p>
            <div className="at-list">
              {tasks.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  occurrences={occurrencesByTask.get(t.id) || []}
                  canManage={canManage}
                  busy={busyId === t.id}
                  onEdit={(task) => { setModalTask(task); setModalOpen(true); }}
                  onToggle={handleToggle}
                  onRunNow={handleRunNow}
                  onDelete={setConfirmDelete}
                  onFixAction={(fix, action) => setFixAction({ fix, action })}
                />
              ))}
            </div>
          </>
        )
      ) : (
        <>
          <div className="at-filters">
            <select className="at-select" value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)} aria-label="Filter by task">
              <option value="">All tasks</option>
              {tasks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <select className="at-select" value={personFilter} onChange={(e) => setPersonFilter(e.target.value)} aria-label="Filter by person">
              <option value="">Everyone</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {tab === "waiting" ? (
            waiting.length === 0 ? (
              <div className="at-empty">
                <strong>Nothing outstanding</strong>
                Every check-in due so far has been answered.
              </div>
            ) : (
              <div className="at-table-wrap">
                <table className="at-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Person</th>
                      <th>Due</th>
                      <th>Waiting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waiting.map((o) => (
                      <tr key={o.id}>
                        <td>{o.title}</td>
                        <td>{o.assignee?.name}</td>
                        <td>{formatWhen(o.dueAt)}</td>
                        <td className="at-muted">{overdueBy(o.dueAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : answered.length === 0 ? (
            <div className="at-empty">
              <strong>No responses yet</strong>
              Answers appear here as soon as the first check-in is due.
            </div>
          ) : (
            <div className="at-table-wrap">
              <table className="at-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Person</th>
                    <th>Due</th>
                    <th>Answer</th>
                    <th>Answered</th>
                  </tr>
                </thead>
                <tbody>
                  {answered.map((o) => (
                    <tr key={o.id}>
                      <td>{o.title}</td>
                      <td>{o.assignee?.name}</td>
                      <td>{formatWhen(o.dueAt)}</td>
                      <td><Answer occ={o} /></td>
                      <td className="at-muted">
                        {o.respondedAt ? formatWhen(o.respondedAt) : "—"}
                        {o.respondedVia && <span> · {o.respondedVia}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modalOpen && (
        <AllottedTaskModal
          task={modalTask}
          onClose={() => setModalOpen(false)}
          onSaved={(msg) => { setModalOpen(false); setToast({ msg, type: "success" }); load(); }}
        />
      )}

      {fixAction && fixAction.action !== "close" && (
        <FixActionDialog
          fix={fixAction.fix}
          action={fixAction.action}
          users={fixPeople}
          onClose={() => setFixAction(null)}
          onDone={(msg) => { setFixAction(null); setToast({ msg, type: "success" }); load(); }}
        />
      )}

      {fixAction?.action === "close" && (
        <ConfirmDialog
          title="Close this fix?"
          message={`Nobody will be reminded about it any more. A new "No" on "${fixAction.fix.title}" will raise a fresh fix.`}
          confirmLabel="Close fix"
          onConfirm={handleCloseFix}
          onClose={() => setFixAction(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this task?"
          message={`"${confirmDelete.title}" and every response recorded against it will be removed. This can't be undone.`}
          confirmLabel="Delete task"
          confirmClass="btn-danger"
          onConfirm={handleDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
